import { OSEhrState, OSEhrStateWithPII } from '@getvim/vim-os-api';
import { AppEhrState, PatientWithPII } from '../types/ehrState';
import {
  EhrResourceEnhancementsExecutor,
  OSToAppEhrConvertor,
  OsToExternalAppEhrConvertor,
} from '@getvim/dynamic-vim-os-sdk/sdk';
import { EHR } from '@getvim/vim-os-sdk-api';

type EHR_STATE = AppEhrState<{ withPII: true }>;

export class OsToInternalAppEhrConvertor implements OSToAppEhrConvertor<EHR_STATE> {
  convertPatientWithPII = (
    osPatient: NonNullable<OSEhrStateWithPII['patient']>,
    ehrResourceEnhancementsExecutor: EhrResourceEnhancementsExecutor<EHR_STATE>,
  ): PatientWithPII => {
    const { patientSessionId, token, contentSupplierIdentifiers } = osPatient;
    const externalPatient = this.externalConvertor.convertPatientWithPII(
      osPatient,
      ehrResourceEnhancementsExecutor,
    );
    const appPatient: PatientWithPII = {
      ...externalPatient,
      token,
      patientSessionId,
      contentSupplierIdentifiers,
    };
    if (appPatient.insurance) {
      appPatient.insurance.insurer = osPatient.insurance?.insurer;
    }
    return appPatient;
  };

  convertReferralWithPII = (
    osReferral: NonNullable<OSEhrStateWithPII['referral']>,
  ): NonNullable<EHR_STATE['referral']> => {
    const { token } = osReferral;
    const externalReferral = this.externalConvertor.convertReferralWithPII(osReferral);
    return {
      ...externalReferral,
      token,
    };
  };

  convertEncounter = (
    osEncounter: NonNullable<OSEhrStateWithPII['encounter']>,
  ): NonNullable<EHR_STATE['encounter']> => {
    const { token } = osEncounter;
    const externalEncounter = this.externalConvertor.convertEncounter(osEncounter);
    return {
      ...externalEncounter,
      token,
    };
  };

  convertOrders = (
    osOrders: NonNullable<OSEhrState['orders']>,
  ): NonNullable<EHR_STATE['orders']> => {
    return this.externalConvertor.convertOrders(osOrders);
  };

  convertClaim = (osClaim: NonNullable<OSEhrState['claim']>): NonNullable<EHR_STATE['claim']> => {
    return this.externalConvertor.convertClaim(osClaim);
  };

  constructor() {
    this.externalConvertor = new OsToExternalAppEhrConvertor();
  }
  convertEhr: OSToAppEhrConvertor<EHR_STATE>['convertEhr'] = (
    { patient, encounter, referral, orders, claim }: OSEhrStateWithPII,
    ehrResourceEnhancementsExecutor,
  ) => {
    return {
      patient: patient && this.convertPatientWithPII(patient, ehrResourceEnhancementsExecutor),
      referral: referral && this.convertReferralWithPII(referral),
      encounter: encounter && this.convertEncounter(encounter),
      orders: orders && this.convertOrders(orders),
      claim: claim && this.convertClaim(claim),
    };
  };

  convertResource: OSToAppEhrConvertor<EHR_STATE>['convertResource'] = (
    resource,
    value,
    ehrResourceEnhancementsExecutor,
  ) => {
    const converters: {
      [K in EHR.EHRResource]: (v: NonNullable<OSEhrStateWithPII[K]>) => NonNullable<EHR_STATE[K]>;
    } = {
      [EHR.EHRResource.patient]: (v) =>
        this.convertPatientWithPII(v, ehrResourceEnhancementsExecutor),
      [EHR.EHRResource.referral]: (v) => this.convertReferralWithPII(v),
      [EHR.EHRResource.encounter]: (v) => this.convertEncounter(v),
      [EHR.EHRResource.orders]: (v) => this.convertOrders(v),
      [EHR.EHRResource.claim]: (v) => this.convertClaim(v),
      [EHR.EHRResource.customEhrData]: (v) => v,
    };
    return converters[resource](value);
  };

  private externalConvertor: OsToExternalAppEhrConvertor;
}
